import React, { useRef } from 'react'
import {
  faHouse, faListCheck, faPalette, faPlug, faRulerCombined, faSolarPanel, faSitemap,
} from '@fortawesome/free-solid-svg-icons'
import useDocumentTitle from '../hooks/useDocumentTitle'
import KreatorSzkielet from '../components/kreator/KreatorSzkielet'
import { dobierzModul } from '../api/falowniki'
import { formatPrice } from '../utils'

/**
 * Asystent doboru modułu PV.
 *
 * ═══ DLACZEGO TA KATEGORIA DZIAŁA JUŻ DZIŚ ═══
 * Kryteria, o które pytamy, mają wysokie pokrycie: moc, rodzaj i kolor ramy dla
 * 100% modeli, cena za wat dla 84%. Sprawność i gwarancja siedzą przy 45-50%
 * i dlatego nie odsiewają, tylko punktują — model bez sprawności nie przegrywa
 * przez naszą dziurę w danych, tylko dostaje wpis w „czego nie wiemy".
 *
 * Pytania dotyczą DACHU I DOMU, nie parametrów: „ile masz miejsca" zamiast
 * „minimalna sprawność", „czy zależy Ci na czarnych" zamiast „kolor ramy".
 *
 * ═══ ROZBUDOWA MA TRZY POZIOMY DOKŁADNOŚCI ═══
 * 1. Znasz model i mamy jego kartę katalogową: dobieramy po PRĄDZIE.
 * 2. Znasz model, ale karty nie mamy: prosimy o Impp z naklejki z tyłu modułu.
 * 3. Została sama moc: dobieramy zgrubnie i mówimy wprost, że to słabsze
 *    kryterium.
 *
 * Kolejne pytania POKAZUJĄ SIĘ SAME w miarę odpowiadania — kto zna model
 * z kartą, nie zobaczy pytania o prąd ani o moc.
 */

// Parametry obecnych modułów są potrzebne TYLKO wtedy, gdy nowe moduły trafią
// na wspólny punkt pracy ze starymi. Na osobnym wejściu MPPT i przy osobnym
// falowniku nie mają żadnego wpływu na uzysk.
const POTRZEBNY_STARY_MODUL = (o) =>
  !['osobny_mppt', 'osobny_falownik'].includes(o.sposob_rozbudowy)

const PYTANIA = [
  {
    klucz: 'instalacja', ikona: faHouse, etykieta: 'Instalacja',
    tytul: 'To nowa instalacja czy dokładasz do istniejącej?',
    opis: 'Przy rozbudowie liczy się dopasowanie elektryczne do modułów, które już masz na dachu, a nie najlepszy stosunek ceny do mocy.',
    opcje: [
      { v: 'nowa', l: 'Nowa instalacja', o: 'Pełna swoboda wyboru' },
      { v: 'rozbudowa', l: 'Dokładam do istniejącej', o: 'Dopasujemy do obecnych' },
    ],
  },
  {
    klucz: 'sposob_rozbudowy', ikona: faSitemap, etykieta: 'Podłączenie',
    // ═══ TO PYTANIE STOI PIERWSZE, JESZCZE PRZED PYTANIEM O OBECNE MODUŁY ═══
    // Bo przy wolnym wejściu MPPT albo osobnym falowniku parametry tego, co już
    // wisi, nie mają znaczenia: każde wejście szuka swojego punktu pracy. Pytanie
    // o model starego modułu byłoby wtedy pracą wykonaną na darmo.
    tytul: 'Jak podłączysz nowe moduły?',
    opis: 'To rozstrzyga, po czym dobieramy. Na osobnym falowniku i na wolnym wejściu MPPT nowe moduły mają własny punkt pracy, więc nie muszą pasować do obecnych. We wspólnym łańcuchu płynie jeden prąd i wtedy liczy się dopasowanie prądu, bo różnica obniża uzysk także tych modułów, które już masz.',
    warunek: (o) => o.instalacja === 'rozbudowa',
    opcjonalne: true,
    opcje: [
      { v: 'osobny_falownik', l: 'Do osobnego falownika', o: 'Nowy falownik tylko dla nowych modułów' },
      { v: 'osobny_mppt', l: 'Na wolne wejście MPPT', o: 'Najlepiej, jeśli falownik ma wolne wejście' },
      { v: 'ten_sam_lancuch', l: 'Do istniejącego łańcucha', o: 'Dopasujemy prąd do obecnych modułów' },
    ],
  },
  {
    klucz: 'moc_istniejacych_w', ikona: faSolarPanel, etykieta: 'Moc obecnych',
    // Moc stoi PRZED pytaniem o model, bo zna ją prawie każdy: jest na fakturze,
    // w umowie przyłączeniowej i na tabliczce. Nazwę modelu pamięta mało kto,
    // a podana moc zawęża listę podpowiedzi tak, że sama marka już wystarcza.
    tytul: 'Jaką moc mają moduły, które już masz?',
    opis: 'Znajdziesz ją na tabliczce z tyłu modułu, na fakturze albo w dokumentacji instalacji. Zawęzi nam listę w następnym pytaniu.',
    warunek: (o) => o.instalacja === 'rozbudowa' && POTRZEBNY_STARY_MODUL(o),
    typ: 'liczba', opcjonalne: true,
    liczba: { jednostka: 'W', min: 100, krok: 5, placeholder: 'np. 450' },
  },
  {
    klucz: 'modul_obecny', ikona: faSolarPanel, etykieta: 'Obecny moduł',
    tytul: 'Który to model?',
    opis: 'Wpisz markę i model, na przykład „ja solar 500". Jeśli mamy jego kartę katalogową, dobierzemy nowy moduł po prądzie, a nie po samej mocy. Nazwy nie znasz? Podaj obok prąd Impp z karty katalogowej albo z naklejki z tyłu modułu.',
    warunek: (o) => o.instalacja === 'rozbudowa' && POTRZEBNY_STARY_MODUL(o),
    opcjonalne: true,
    typ: 'szukanie', kategoria: 'panel',
    alternatywa: 'prad',
    mocZ: (o) => o.moc_istniejacych_w,
    placeholder: 'np. ja solar 500',
  },
  {
    klucz: 'modulow_w_lancuchu', ikona: faSitemap, etykieta: 'Modułów w łańcuchu',
    tytul: 'Ile modułów jest dziś w tym łańcuchu?',
    opis: 'Potrzebne, żeby policzyć napięcie łańcucha po rozbudowie. Zbyt długi łańcuch w mróz przekracza maksymalne napięcie falownika, a to go uszkadza.',
    warunek: (o) => o.sposob_rozbudowy === 'ten_sam_lancuch',
    typ: 'liczba', opcjonalne: true,
    liczba: { jednostka: 'szt.', min: 1, krok: 1, placeholder: 'np. 12' },
  },
  {
    klucz: 'falownik_model', ikona: faPlug, etykieta: 'Falownik',
    tytul: 'Jaki masz falownik?',
    opis: 'Sprawdzimy w jego karcie katalogowej, czy po dołożeniu modułów nie przekroczysz maksymalnego napięcia ani prądu wejścia. Kartę mamy dla czterech na pięć falowników w katalogu. Zawęź listę mocą, rodzajem i producentem albo wpisz nazwę.',
    warunek: (o) => o.instalacja === 'rozbudowa' && o.sposob_rozbudowy !== 'osobny_falownik',
    opcjonalne: true,
    typ: 'szukanie', kategoria: 'inwerter',
    filtry: ['moc', 'rodzaj', 'producent'],
    placeholder: 'np. deye 10 sg05',
  },
  {
    klucz: 'ile_dokladam', ikona: faSolarPanel, etykieta: 'Ile dokładasz',
    tytul: 'Ile chcesz dołożyć?',
    // Instalator liczy sztukami, właściciel domu kilowatami. Przy kilowatach
    // liczba sztuk wychodzi z mocy KONKRETNEGO modułu i rzadko trafia równo:
    // 3 kW to sześć modułów 500 W albo siedem 435 W. Pokazujemy, co wyszło.
    opis: 'Podaj liczbę modułów albo moc w kilowatach. Przy kilowatach liczbę sztuk policzymy z mocy każdej propozycji osobno, więc rzadko wyjdzie równo.',
    warunek: (o) => o.instalacja === 'rozbudowa',
    typ: 'liczba',
    jednostki: [
      { v: 'szt', l: 'Modułów', jednostka: 'szt.', placeholder: 'np. 6' },
      { v: 'kw', l: 'Kilowatów', jednostka: 'kWp', placeholder: 'np. 3' },
    ],
    liczba: { jednostka: 'szt.', min: 0.1, krok: 1 },
  },
  {
    klucz: 'moc_kwp', ikona: faSolarPanel, etykieta: 'Moc instalacji',
    tytul: 'Jakiej mocy instalacji szukasz?',
    opis: 'Podaj moc w kilowatach. Liczbę modułów i koszt całości policzymy przy każdej propozycji, bo zależą od mocy konkretnego modułu.',
    warunek: (o) => o.instalacja !== 'rozbudowa',
    typ: 'liczba',
    liczba: { jednostka: 'kWp', min: 1, krok: 0.5, placeholder: 'np. 8' },
  },
  {
    klucz: 'montaz', ikona: faHouse, etykieta: 'Montaż',
    tytul: 'Gdzie staną moduły?',
    opis: 'Na gruncie dobieramy wyłącznie moduły dwustronne: zbierają odbicie od podłoża i dają realny zysk, którego na dachu skośnym nie ma.',
    opcje: [
      { v: 'dach', l: 'Na dachu', o: 'Bez ograniczeń co do rodzaju' },
      { v: 'grunt', l: 'Na gruncie', o: 'Tylko moduły dwustronne' },
    ],
  },
  {
    klucz: 'miejsce', ikona: faRulerCombined, etykieta: 'Miejsce',
    tytul: 'Ile masz miejsca?',
    opis: 'Przy ograniczonej powierzchni liczy się moc z metra kwadratowego, więc premiujemy wyższą sprawność i mocniejsze moduły.',
    opcje: [
      { v: 'duzo', l: 'Wystarczająco', o: 'Liczy się cena za wat' },
      { v: 'malo', l: 'Mało, muszę wycisnąć maksimum', o: 'Liczy się sprawność i moc' },
    ],
  },
  {
    klucz: 'estetyka', ikona: faPalette, etykieta: 'Wygląd',
    tytul: 'Czy wygląd modułów ma znaczenie?', opis: '',
    opcje: [
      { v: 'dowolne', l: 'Nie ma znaczenia', o: 'Cały katalog' },
      { v: 'czarne', l: 'Ma znaczenie', o: 'Full black albo czarna rama' },
    ],
  },
  {
    klucz: 'priorytet', ikona: faListCheck, etykieta: 'Priorytet',
    tytul: 'Co jest dla Ciebie ważniejsze?', opis: '',
    opcje: [
      { v: 'ekonomia', l: 'Najniższa cena za wat', o: 'Cena waży najmocniej' },
      { v: 'balans', l: 'Rozsądny balans', o: 'Cena i parametry po równo' },
      { v: 'jakosc', l: 'Maksymalna jakość', o: 'Sprawność i gwarancja przed ceną' },
    ],
  },
]

/**
 * Pytania typu `szukanie` trzymają cały wybrany model (`{id, etykieta, ma_karte}`),
 * bo pasek postępu pokazuje nazwę. Backend potrzebuje samego identyfikatora.
 */
const doKryteriow = (o) => {
  const { modul_obecny: modul, falownik_model: falownik, ile_dokladam: ile, ...reszta } = o
  const wSztukach = !ile?.jednostka || ile.jednostka === 'szt'
  return {
    priorytet: 'balans',
    ...reszta,
    model_obecny_id: modul?.model?.id ?? null,
    impp_obecnych_a: modul?.impp ?? null,
    falownik_model_id: falownik?.model?.id ?? null,
    // Kilowaty idą tą samą drogą co przy nowej instalacji: liczbę sztuk liczy
    // backend z mocy każdej propozycji osobno.
    liczba_modulow: ile?.ile && wSztukach ? ile.ile : null,
    moc_kwp: ile?.ile && !wSztukach ? ile.ile : (reszta.moc_kwp ?? null),
  }
}

const OPIS_ODNIESIENIA = {
  karta: 'Dobór oparliśmy na karcie katalogowej modułu, który już masz.',
  naklejka: 'Dobór oparliśmy na prądzie Impp, który podałeś z naklejki modułu.',
  katalog: 'Model rozpoznaliśmy w katalogu, ale nie mamy jego karty, więc porównujemy po mocy.',
  moc: 'Mamy tylko moc obecnych modułów, więc dopasowanie jest zgrubne. Prąd Impp z naklejki dałby dokładniejszy wynik.',
}

const OPIS_SPOSOBU = {
  osobny_falownik: 'Nowe moduły dostaną własny falownik, więc parametry obecnych nie miały wpływu na dobór.',
  osobny_mppt: 'Przy osobnym wejściu MPPT moduły nie muszą pasować do siebie elektrycznie, bo każde wejście szuka własnego punktu pracy.',
  ten_sam_lancuch: 'We wspólnym łańcuchu płynie jeden prąd, więc dopasowywaliśmy Impp. Różnica prądów obniża uzysk także tych modułów, które już masz.',
}

// `osadzony`: strona w oknie nad Projektem 3D (uwaga F7, 2026-10-25): odpowiedzi startowe z modelu, bez zmiany tytułu
// karty, a „Użyj w projekcie” przy propozycji oddaje ją do `onUzyjModelu`.
const DoborModulu = ({ osadzony = false, odpowiedziStartowe = null, onUzyjModelu = null }) => {
  useDocumentTitle(osadzony ? 'Projekt 3D' : 'Asystent doboru modułów PV')
  // Odpowiedzi idą do Projektu 3D razem z propozycją (montaż na gruncie, uwaga G1).
  const odpowiedzi = useRef({})
  return (
    <KreatorSzkielet
      onZmiana={onUzyjModelu ? (o) => { odpowiedzi.current = o } : null}
      tytul="Asystent doboru modułów PV"
      opis="Odpowiedz na kilka pytań o swój dach. Przejrzymy cały katalog i pokażemy trzy propozycje z uzasadnieniem, dlaczego akurat te."
      pytania={PYTANIA}
      domyslne={osadzony ? odpowiedziStartowe || {} : {}}
      tekstPrzycisku="Pokaż dopasowane moduły"
      onDobierz={(o) => dobierzModul(doKryteriow(o))}
      konfiguracjaWynikow={(wynik) => ({
        kategoria: 'panel',
        cenaEtykieta: 'za sztukę, od',
        wiersze: [
          // Liczba sztuk i koszt całości NAJWYŻEJ, bo to jest liczba, którą
          // porównuje kupujący. Cena pojedynczego modułu bez liczby sztuk
          // wprowadza w błąd: tańszy moduł o mniejszej mocy wymaga ich więcej.
          ['Potrzeba sztuk', (p) => (p.sztuk ? `${p.sztuk} szt.` : null)],
          ['Koszt samych modułów', (p) => (p.koszt_calosci ? `${formatPrice(p.koszt_calosci)} zł` : null)],
          ['Moc instalacji', (p) => (p.moc_instalacji_kwp ? `${String(p.moc_instalacji_kwp).replace('.', ',')} kWp` : null)],
          ['Moc modułu', (p) => `${p.moc_w} W`],
          ['Cena za wat', (p) => (p.cena_za_wp ? `${String(p.cena_za_wp).replace('.', ',')} zł/Wp` : null)],
          ['Sprawność', (p) => (p.sprawnosc ? `${String(p.sprawnosc).replace('.', ',')}%` : null)],
          ['Rodzaj', (p) => [p.typ, p.typ_dodatkowy].filter(Boolean).join(', ')],
          ['Kolor ramy', (p) => p.kolor_ramy],
          ['Gwarancja produktowa', (p) => (p.gwarancja_lat ? `${p.gwarancja_lat} lat` : null)],
          ['Wymiary', (p) => p.wymiary],
          ['Waga', (p) => (p.waga_kg ? `${String(p.waga_kg).replace('.', ',')} kg` : null)],
          ['Liczba ofert', (p) => `${p.ofert}`],
        ],
        cta: (p) => `Zobacz ${p.ofert} ofert`,
        ...(onUzyjModelu ? { onUzyj: (p) => onUzyjModelu(p, odpowiedzi.current) } : {}),
        stopka: [
          wynik.odniesienie ? OPIS_ODNIESIENIA[wynik.odniesienie.zrodlo] : null,
          OPIS_SPOSOBU[wynik.sposob_rozbudowy] || null,
          wynik.falownik && !wynik.falownik.ma_karte
            ? `Falownika ${wynik.falownik.nazwa} nie mamy w kartach katalogowych, więc napięć nie przeliczyliśmy.`
            : null,
          'Liczba sztuk i koszt całości liczone dla podanej mocy instalacji. Ocena bierze pod uwagę tylko te parametry, które mamy w katalogu, a brak danych nie obniża oceny modułu.',
        ].filter(Boolean).join(' '),
      })}
    />
  )
}

export default DoborModulu
